import {
  httpsCallable,
  HttpsCallableResult,
  FunctionsError,
} from 'firebase/functions'
import { functions } from '../firebase'
import { z } from 'zod'
import { CHAIN } from '@tonconnect/ui-react'
import { UserCard } from '../../../common/schema/users/userCards'
import { Fragment } from '../../../common/schema/users/fragments'
import { Timestamp } from 'firebase/firestore'

export const CheckProofRequestSchema = z.object({
  address: z.string(),
  network: z.enum([CHAIN.MAINNET, CHAIN.TESTNET]),
  public_key: z.string(),
  proof: z.object({
    timestamp: z.number(),
    domain: z.object({
      lengthBytes: z.number(),
      value: z.string(),
    }),
    payload: z.string(),
    signature: z.string(),
    state_init: z.string(),
  }),
})

export type CheckProofRequest = z.infer<typeof CheckProofRequestSchema>

export interface CheckProofResponse {
  success: boolean
  customToken: string
}

type aeonOrderRequest = {
  cardPackId: string
}

// This is full response from aeon
// interface aeonOrderResponse {
//   code: string
//   msg: string
//   model: {
//     webUrl: string
//     orderNo: string
//   }
//   traceId: string
//   success: boolean
//   error: boolean
// }

interface aeonOrderResponse {
  webUrl: string
  orderNo: string
}

type openRequest = {
  userCardPackId: string
}

interface openResponse {
  success: boolean
  cards: UserCard[]
}

type openCardRequest = {
  userCardId: string
}

interface openCardResponse {
  success: boolean
  fragment: Fragment
  tokenReward: number
  revealedCard: UserCard
  newUnrevealedCard?: UserCard
}

type forgeCardRequest = {
  userCardIds: string[]
}

interface forgeCardResponse {
  success: boolean
  forgeSuccess: boolean
  cardsConsumed: number
  tokensCost: number
  resultCard: UserCard
}

/** Request type for processing referral codes */
export interface processReferralRequest {
  /** Referral code to process */
  referralCode: string
}

/** Response type for referral processing */
export interface processReferralResponse {
  /** Whether the referral was successfully processed */
  success: boolean
  /** Amount of tokens awarded to the referrer */
  referrerReward: number
  /** Amount of tokens awarded to the referee (user applying the code) */
  refereeReward: number
  /** Optional milestone reward information */
  milestone?: {
    /** Number of referrals achieved for this milestone */
    count: number
    /** Bonus reward amount for reaching this milestone */
    reward: number
  }
  /** Referrer's updated tier information */
  tier?: {
    /** New tier achieved (if changed) */
    newTier: 'bronze' | 'silver' | 'gold' | 'diamond'
    /** Progress towards next tier */
    progress: number
    /** Number of referrals needed for next tier */
    nextTierAt: number
  }
}

type mintNftRequest = {
  fragmentId: string
  fragmentCount: number
}

interface mintNftResponse {
  success: boolean
  mintedNft: {
    nftId: string
    fragmentId: string
    fragmentCount: number
    rarity: string
    name: string
    imageUrl: string
    ipfsHash: string
    mintedAt: Timestamp
    onChainAddress: string
  }
}

export type FunctionsErrorWithData = FunctionsError & {
  data: Record<string, unknown>
}

export type CallableFunctionResponse<R> =
  | {
      success: true
      response: HttpsCallableResult<R>
    }
  | {
      success: false
      error: FunctionsErrorWithData
    }

async function callCallableFunction<Request, Response>(
  functionName: string,
  request: Request
): Promise<CallableFunctionResponse<Response>> {
  const callableFunction = httpsCallable<Request, Response>(
    functions,
    functionName
  )
  try {
    const response = await callableFunction(request)
    return { success: true, response }
  } catch (error: unknown) {
    if (
      error &&
      typeof error === 'object' &&
      'code' in error &&
      'message' in error
    ) {
      // error from calling callableFunction is a FunctionsError
      const callableError: FunctionsErrorWithData =
        error as FunctionsErrorWithData
      try {
        // Parse the error message as JSON and place in error.data for convenience
        const message = (error as { message: string }).message
        callableError.data = JSON.parse(message)
      } catch {
        // JSON parse error
        if (
          error &&
          typeof error === 'object' &&
          'message' in error &&
          typeof (error as { message: unknown }).message === 'string'
        ) {
          callableError.data = { error: (error as { message: string }).message }
        } else {
          console.error('Error parsing response', error)
          callableError.data = { error: 'Unexpected invalid JSON received' }
        }
      }
      return { success: false, error: callableError }
    }

    // error from calling callableFunction is not a FunctionsError
    const genericError = error as FunctionsErrorWithData
    genericError.data = { error: 'Unexpected error' }
    return { success: false, error: genericError }
  }
}

export async function callCheckProof(
  params: CheckProofRequest
): Promise<CallableFunctionResponse<CheckProofResponse>> {
  return callCallableFunction<CheckProofRequest, CheckProofResponse>(
    'auth-checkProof',
    params
  )
}

export async function callCreateOrder(
  params: aeonOrderRequest
): Promise<CallableFunctionResponse<aeonOrderResponse>> {
  return callCallableFunction<aeonOrderRequest, aeonOrderResponse>(
    'cardPacks-aeonOrder',
    params
  )
}

export async function callOpen(
  params: openRequest
): Promise<CallableFunctionResponse<openResponse>> {
  return callCallableFunction<openRequest, openResponse>(
    'cardPacks-open',
    params
  )
}

export async function callOpenCard(
  params: openCardRequest
): Promise<CallableFunctionResponse<openCardResponse>> {
  return callCallableFunction<openCardRequest, openCardResponse>(
    'cards-open',
    params
  )
}

export async function callForge(
  params: forgeCardRequest
): Promise<CallableFunctionResponse<forgeCardResponse>> {
  return callCallableFunction<forgeCardRequest, forgeCardResponse>(
    'cards-forge',
    params
  )
}

/**
 * Process a referral code and award rewards to both parties
 * @param params Request parameters containing the referral code
 * @returns Response with reward and milestone information
 */
export async function callProcessReferral(
  params: processReferralRequest
): Promise<CallableFunctionResponse<processReferralResponse>> {
  return callCallableFunction<processReferralRequest, processReferralResponse>(
    'auth-processReferral',
    params
  )
}

export async function callMintNft(
  params: mintNftRequest
): Promise<CallableFunctionResponse<mintNftResponse>> {
  return callCallableFunction<mintNftRequest, mintNftResponse>(
    'fragments-mintNft',
    params
  )
}
